import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, TextField, MenuItem, IconButton } from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Default Leaflet marker icons reference image files that don't survive bundling —
// point them at the CDN copies instead, otherwise markers render as broken images.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});

export const DEFAULT_CENTER = [1.3521, 103.8198]; // Singapore — a neutral starting point

// Moves the pin to wherever the user clicks the map
const MapClickHandler = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick([e.latlng.lat, e.latlng.lng]) });
  return null;
};

// Leaflet only honours `center` on mount — this recentres the map (zoomed in) when
// the pin is first set or changes, e.g. after geolocation resolves a moment after
// the page/dialog opens.
const RecenterOnPin = ({ pin }) => {
  const map = useMap();
  useEffect(() => {
    if (pin) map.setView(pin, Math.max(map.getZoom(), 16));
  }, [pin, map]);
  return null;
};

// event_date is a plain calendar date (backend sends it as a raw 'YYYY-MM-DD'
// string, see database.js's dateStrings config) — parse it as a string, never
// through `new Date()`, which would reinterpret it in the browser's local
// timezone and can shift it onto the wrong day entirely.
export const toYMD = (value) => (value ? String(value).slice(0, 10) : '');
export const formatDate = (value) => {
  const ymd = toYMD(value);
  if (!ymd) return '-';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
};
export const todayYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const hasCoords = (ev) => ev && ev.lat && ev.lang && !isNaN(parseFloat(ev.lat)) && !isNaN(parseFloat(ev.lang));

// event_time is a free-text varchar; the consumer app stores/shows it as "6:00 AM".
// Offer a dropdown of every 15 minutes from 6:00 AM to 10:00 PM, and normalise any
// incoming value (incl. "09:00" 24h) to the same "h:mm AM/PM" shape so it lines up
// with an option.
export const TIME_OPTIONS = (() => {
  const out = [];
  for (let mins = 6 * 60; mins <= 22 * 60; mins += 15) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hr = h % 12 || 12;
    out.push(`${hr}:${String(m).padStart(2, '0')} ${ampm}`);
  }
  return out;
})();

export const normalizeTime = (value) => {
  const s = String(value || '').trim();
  const m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return '';
  let h = parseInt(m[1], 10);
  const min = m[2];
  const ampm = (m[3] || '').toUpperCase();
  if (ampm === 'PM' && h < 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  const outAmpm = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return `${hr}:${min} ${outAmpm}`;
};

export const emptyEventForm = () => ({ event_date: todayYMD(), event_time: '6:00 AM', event_desc: '' });

// Shared Details + Location fields used by both the "New Event" page and the
// Event Library's edit dialog, so the two stay visually and behaviourally
// identical instead of drifting apart as two separate copies.
export const EventFormFields = ({ form, setForm, pin, setPin, disabled }) => {
  const markerRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const handleCopyCoords = () => {
    if (!pin) return;
    navigator.clipboard.writeText(`${pin[0].toFixed(5)}, ${pin[1].toFixed(5)}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleMarkerDragEnd = () => {
    const m = markerRef.current;
    if (m) {
      const ll = m.getLatLng();
      setPin([ll.lat, ll.lng]);
    }
  };

  return (
    <>
      <Typography variant="overline" color="primary" sx={{ letterSpacing: 1 }}>Details</Typography>
      <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 1, flexWrap: 'wrap' }}>
        <TextField
          label="Date"
          type="date"
          size="small"
          value={form.event_date}
          onChange={(e) => setForm({ ...form, event_date: e.target.value })}
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
          sx={{ flex: '1 1 180px' }}
        />
        <TextField
          label="Time"
          select
          size="small"
          value={form.event_time}
          onChange={(e) => setForm({ ...form, event_time: e.target.value })}
          disabled={disabled}
          sx={{ flex: '1 1 180px' }}
          SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
        >
          {(TIME_OPTIONS.includes(form.event_time) ? TIME_OPTIONS : [form.event_time, ...TIME_OPTIONS])
            .map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
      </Box>
      <TextField
        label="Description / note"
        fullWidth
        size="small"
        multiline
        minRows={3}
        value={form.event_desc}
        onChange={(e) => setForm({ ...form, event_desc: e.target.value })}
        disabled={disabled}
        sx={{ mb: 3 }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="overline" color="primary" sx={{ letterSpacing: 1 }}>Location</Typography>
        {pin ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {`${pin[0].toFixed(5)}, ${pin[1].toFixed(5)}`}
            </Typography>
            <IconButton size="small" onClick={handleCopyCoords}>
              <ContentCopyIcon fontSize="small" color={copied ? 'success' : 'inherit'} />
            </IconButton>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">Click the map or drag the pin</Typography>
        )}
      </Box>
      <Box
        sx={{
          height: 440,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
        }}
      >
        <MapContainer center={pin || DEFAULT_CENTER} zoom={pin ? 16 : 3} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onPick={setPin} />
          <RecenterOnPin pin={pin} />
          {pin && (
            <Marker
              position={pin}
              draggable
              eventHandlers={{ dragend: handleMarkerDragEnd }}
              ref={markerRef}
            />
          )}
        </MapContainer>
      </Box>
    </>
  );
};
